import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.configuration_api.places;

// Helper function to configure the Axios instance
const api: AxiosInstance = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Wrap with cache - using long TTL since places data rarely changes
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'configuration_api',
  group: 'places',
  defaultTtl: '1h', // 1 hour cache since countries/states/cities rarely change
  hashLargeSegments: true,
  emitMeta: true,
});

// Types
export interface Country {
  country_id: number;
  country_name: string;
  country_code?: string;
}

export interface State {
  state_id: number;
  state_name: string;
  country_id: number;
}

export interface City {
  city_id: number;
  city_name: string;
  state_id: number;
}

// Function to fetch all countries (cached)
export const getAllCountries = async (): Promise<AxiosResponse<Country[]>> => {
  return await deduplicateRequest(
    'getAllCountries',
    () => retryWithBackoff(() => 
      cachedApi.get<Country[]>('/GetAllCountries', undefined, { ttl: '1h' })
    )
  );
};

// Function to fetch all states (cached)
export const getAllStates = async (): Promise<AxiosResponse<State[]>> => {
  return await deduplicateRequest(
    'getAllStates',
    () => retryWithBackoff(() => 
      cachedApi.get<State[]>('/GetAllStates', undefined, { ttl: '1h' })
    )
  );
};

// Function to fetch all cities (cached)
export const getAllCities = async (): Promise<AxiosResponse<City[]>> => {
  return await deduplicateRequest(
    'getAllCities',
    () => retryWithBackoff(() => 
      cachedApi.get<City[]>('/GetAllCities', undefined, { ttl: '1h' })
    )
  );
};

// Function to fetch country by ID (cached)
export const getCountryById = async (country_id: number): Promise<AxiosResponse<Country>> => {
  return await deduplicateRequest(
    `getCountryById:${country_id}`,
    () => retryWithBackoff(() => 
      cachedApi.get<Country>(`/GetCountryById/${country_id}`, undefined, { ttl: '1h' })
    )
  );
};

// Function to fetch state by ID (cached)
export const getStateById = async (state_id: number): Promise<AxiosResponse<State>> => {
  return await deduplicateRequest(
    `getStateById:${state_id}`,
    () => retryWithBackoff(() => 
      cachedApi.get<State>(`/GetStateById/${state_id}`, undefined, { ttl: '1h' })
    )
  );
};

// Function to fetch city by ID (cached)
export const getCityById = async (city_id: number): Promise<AxiosResponse<City>> => {
  return await deduplicateRequest(
    `getCityById:${city_id}`,
    () => retryWithBackoff(() => 
      cachedApi.get<City>(`/GetCityById/${city_id}`, undefined, { ttl: '1h' })
    )
  );
};

// Clear cache when needed (e.g., after admin updates)
export const clearPlacesCache = async (): Promise<void> => {
  await cachedApi.clear();
};


